import { ChevronRightIcon } from "@xola/icons";
import { CellClassParams, ColDef, IRowNode, ITooltipParams, RedrawRowsParams } from "ag-grid-community";
import clsx from "clsx";
import { DateFormat, isEquipmentBased, isEquipmentItem, isStringEqual } from "common";
import { isNil, isNumber, snakeCase, uniq, uniqWith } from "lodash";
import React from "react";
import { DigitalWaiver, Experience, Field, Form, UnitType } from "types";
import "./RosterGuestList.css";
import { formatTime } from "@xola/ui-kit";
import { CollapseHeaderComponent } from "../../DashboardTable/CollapseHeader";
import { useDashboardTableStore } from "../../DashboardTable/useDashboardTableStore";
import { ActionCellRenderer } from "./ActionCell";
import { AddOnsCellRenderer, addOnsCellValueGetter } from "./AddOnsCell";
import { AddOnsPickUpCellRenderer, addOnsPickUpCellValueGetter } from "./AddOnsPickUpCell";
import { AmountDueCellRenderer, amountDueCellValueGetter } from "./AmountDueCell";
import { CheckInStatusCellRenderer, checkInStatusValueGetter } from "./CheckInStatusCell";
import { DemographicCellRenderer, demographicCellValueGetter } from "./DemographicCell";
import { emailCellValueGetter } from "./EmailCell";
import { EquipmentCellRenderer, equipmentCellValueGetter } from "./EquipmentCell";
import { GrossPurchaseAmountCellFormatter, grossPurchaseAmountCellValueGetter } from "./GrossPurchaseAmountCell";
import {
    GrossReservationAmountCellFormatter,
    grossReservationAmountCellValueGetter,
} from "./GrossReservationAmountCell";
import { GuestCellRenderer, guestCellValueGetter } from "./GuestCell";
import { NotesCellRenderer, notesCellValueGetter } from "./NotesCell";
import { PhoneCellFormatter, phoneCellValueGetter } from "./PhoneCell";
import { dateComparator, PurchaseDateCellFormatter, purchaseDateCellValueGetter } from "./PurchaseDateCell";
import { QuestionnaireCellRenderer, questionnaireCellValueGetter } from "./QuestionnaireCell";
import { ReservationCellFormatter, reservationCellValueGetter } from "./ReservationCodeCell";
import { SourceCellRenderer, sourceCellValueGetter } from "./SourceCell";
import { TagsCellFormatter, tagsCellValueGetter } from "./TagsCell";
import { VoucherCellFormatter, voucherCellValueGetter } from "./VouchersCell";
import { waiverCellValueGetter, WaiversCellRenderer } from "./WaiverCell";
import { XOLA_DEFAULT_CONFIG_NAME } from "@/hooks/questionnaireFormHooks";
import { isCheckInAllowed } from "@/models/Experience";
import { isProductHasLinkedWaiverTemplate } from "@/models/Waiver";
import { isTableView, useDashboardRosterStore } from "@/stores/dashboardFilterStore";
import { canViewAmountDue, canViewGuestStatus, canViewPurchase, canViewWaivers } from "@/stores/globalStore";
import { RosterViewConfiguration } from "@/types/Dashboard";
import { getCollection } from "@/utils/getCollection";
import { getSeller } from "@/utils/getSeller";

/**
 * The method gets value for the tooltip from the cell or uses the message passed instead.
 * @param message
 * @returns tooltip value getter function
 */
export const getTooltipText = (message?: string) => (params: ITooltipParams) => {
    return params.value ? message ?? params.value : null;
};

const getCellClass = ({ value }: CellClassParams) => {
    return value ? "ui-copy-text" : "";
};

const isSummaryRow = (node: IRowNode<any>) => node.data?.path?.[0] === "summary";

const timeCellComparator = (a: number, b: number, nodeA: IRowNode, nodeB: IRowNode): number => {
    if (isSummaryRow(nodeA) || isSummaryRow(nodeB)) return 0;

    if (nodeA.level !== nodeB.level) return 0;

    const aIsNil = isNil(a);
    const bIsNil = isNil(b);

    if (aIsNil && !bIsNil) return -1;
    if (!aIsNil && bIsNil) return 1;
    if (aIsNil && bIsNil) return 0;

    return a - b;
};

const guestCellComparator = (
    valueA: { name: string } | undefined,
    valueB: { name: string } | undefined,
    nodeA: IRowNode<any>,
    nodeB: IRowNode<any>,
) => {
    if (isSummaryRow(nodeA) || isSummaryRow(nodeB)) return 0;

    if (nodeA.level !== nodeB.level) return 0;

    const nameA = valueA?.name ?? "";
    const nameB = valueB?.name ?? "";
    return nameA.localeCompare(nameB);
};

export enum RosterColumnId {
    COLLAPSE = "collapse",
    ACTION = "action",
    PRODUCT = "product",
    TIME = "time",
    GUEST = "guest_name",
    DEMOGRAPHICS = "demographics",
    EQUIPMENT = "equipment",
    BALANCE_DUE = "balance_due",
    NOTES = "notes",
    CHECK_IN = "check_in",
    WAIVERS = "waivers",
    SOURCE = "source",
    VOUCHERS = "vouchers",
    PHONE = "phone",
    EMAIL = "email",
    ADDONS = "addons",
    TAGS = "tags",
    RESERVATION_CODE = "reservation_code",
    GROSS_PURCHASE_AMOUNT = "gross_purchase_amount",
    GROSS_RESERVATION_AMOUNT = "gross_reservation_amount",
    PURCHASE_DATE = "purchase_date",
}

export enum HeaderNames {
    ACTION = "Action",
    PRODUCT = "Product",
    TIME = "Time",
    GUEST = "Guest Name",
    DEMOGRAPHICS = "Demographics",
    EQUIPMENT = "Equipment",
    BALANCE_DUE = "Balance Due",
    NOTES = "Notes",
    CHECK_IN = "Check In",
    WAIVERS = "Waivers",
    SOURCE = "Source",
    VOUCHERS = "Vouchers",
    PHONE = "Phone",
    EMAIL = "Email",
    ADD_ONS = "Add-ons",
    TAGS = "Tags",
    RESERVATION_CODE = "Reservation Code",
    GROSS_PURCHASE_AMOUNT = "Gross Purchase Amount",
    GROSS_RESERVATION_AMOUNT = "Gross Reservation Amount",
    PURCHASE_DATE = "Purchase Date",
}

export const getColumnDefs = (
    viewConfigurations: RosterViewConfiguration[],
    experience?: Experience,
    experiences?: Experience[],
) => {
    const isTable = isTableView();
    const seller = getSeller();
    const forms = getCollection<Form>("forms");
    const waiverTemplates = getCollection<DigitalWaiver>("waiverTemplates");
    const { selectedTableView } = useDashboardTableStore.getState();
    const { selectedRosterView } = useDashboardRosterStore.getState();
    const selectedView = isTable ? selectedTableView : selectedRosterView;

    const favoriteViewConfiguration = viewConfigurations.find((viewConfiguration) => viewConfiguration.favourite);

    const selectedViewConfiguration = viewConfigurations.find(
        (viewConfiguration) => viewConfiguration.id === selectedView,
    );

    const viewConfiguration = selectedViewConfiguration ?? favoriteViewConfiguration;

    let defaultColDefs = columnDefs.map((colDef) => ({ ...colDef }));

    if (viewConfiguration?.columns) {
        const columnOrderSet = new Set(viewConfiguration.columns.map((col) => col.id));
        defaultColDefs = defaultColDefs.map((colDef) => {
            colDef.hide = colDef.colId && !columnOrderSet.has(colDef.colId) ? true : false;
            return colDef;
        });
    }

    // Apply hide logic based on permissions and conditions
    const hideCheckInGuest =
        !canViewGuestStatus() ||
        (Boolean(experience && !isCheckInAllowed(seller, experience)) && !experience?.allowCheckoutAtArrival);
    const hideActionCell = !canViewPurchase();
    const hideAmountDue = !canViewAmountDue();

    const isWaiverLinked =
        experience && waiverTemplates
            ? isProductHasLinkedWaiverTemplate(waiverTemplates, experience)
            : canViewWaivers();
    const hideWaivers = !isWaiverLinked;

    defaultColDefs = defaultColDefs.map((colDef) => {
        switch (colDef.colId ?? "") {
            case RosterColumnId.ACTION: {
                colDef.hide = colDef.hide ?? hideActionCell;
                break;
            }
            case RosterColumnId.CHECK_IN: {
                colDef.hide = colDef.hide ?? hideCheckInGuest;
                break;
            }
            case RosterColumnId.BALANCE_DUE: {
                colDef.hide = colDef.hide ?? hideAmountDue;
                break;
            }
            case RosterColumnId.WAIVERS: {
                colDef.hide = colDef.hide ?? hideWaivers;
                break;
            }
            // Hide these columns for all tables except DashboardTable.
            case RosterColumnId.PRODUCT:
                colDef.hide = isTable ? colDef.hide : true;
                break;
            case RosterColumnId.TIME:
                colDef.hide = isTable ? colDef.hide : true;
                break;

            default:
                break;
        }

        return colDef;
    });

    if (!forms) {
        return { data: defaultColDefs };
    }

    // Rendering unique questionnaire Columns Start
    const uniqueFields = getFormUniqFields(forms);

    const formColDefs = uniqueFields.map((uniqueField) => ({
        headerName: uniqueField.label,
        colId: snakeCase(uniqueField.label),
        minWidth: 130,
        hide: viewConfiguration?.columns?.find((col) => col.id === snakeCase(uniqueField.label)) ? false : true,
        sortable: false,
        valueGetter: questionnaireCellValueGetter,
        cellRenderer: QuestionnaireCellRenderer,
        valueGetterParams: { headerName: uniqueField.label },
        cellRendererParams: { headerName: uniqueField.label, fieldType: uniqueField.type },
    }));
    defaultColDefs.push(...formColDefs);
    // Rendering unique questionnaire Columns End

    if (experience && isEquipmentBased(experience)) {
        // Show Equipment column; keep Demographics for attendees (demographic_secondary)
        defaultColDefs = defaultColDefs.map((col) => {
            if (col.colId === RosterColumnId.EQUIPMENT) return { ...col, hide: false };
            return col;
        });
    }

    // Rendering unique Pick Up Add-ons Columns Start
    let uniqueAddOnsPickupFields: PickUpAddOnsUniqField[] = [];
    if (experience) {
        uniqueAddOnsPickupFields = getPickUpAddOnsUniqFields(experience);
    } else if (experiences && experiences.length > 0) {
        uniqueAddOnsPickupFields = getPickUpAddOnsUniqFieldsFromMultiple(experiences);
    }

    const addOnsIndex = defaultColDefs.findIndex((col) => col.colId === RosterColumnId.ADDONS);
    const isXolaDefaultView = viewConfiguration?.id === XOLA_DEFAULT_CONFIG_NAME;

    const pickupColDefs = uniqueAddOnsPickupFields.map((uniqueField) => ({
        headerName: uniqueField.label,
        colId: snakeCase(uniqueField.label),
        minWidth: 145,
        hide: !isXolaDefaultView && !viewConfiguration?.columns?.find((col) => col.id === snakeCase(uniqueField.colId)),
        sortable: false,
        valueGetter: addOnsPickUpCellValueGetter,
        cellRenderer: AddOnsPickUpCellRenderer,
        valueGetterParams: { headerName: uniqueField.label },
        cellRendererParams: { headerName: uniqueField.label },
    }));

    if (addOnsIndex >= 0) {
        defaultColDefs.splice(addOnsIndex + 1, 0, ...pickupColDefs);
    } else {
        defaultColDefs.push(...pickupColDefs);
    }

    // Re-sort after dynamic columns (questionnaire, pickup add-ons) are injected so their
    // saved position in viewConfiguration.columns is honoured.
    if (viewConfiguration?.columns) {
        const columnOrder = viewConfiguration.columns.map((col) => col.id);
        defaultColDefs.sort((a, b) => {
            const indexA = columnOrder.indexOf(a?.colId ?? "");
            const indexB = columnOrder.indexOf(b?.colId ?? "");
            return (
                (indexA >= 0 ? indexA : Number.POSITIVE_INFINITY) - (indexB >= 0 ? indexB : Number.POSITIVE_INFINITY)
            );
        });
    }

    const columnState = useDashboardTableStore.getState().columnState;
    const isCustomized = useDashboardTableStore.getState().isColumnDefsCustomized;
    let mergedColumnDefs = defaultColDefs;

    // Merge state into defs
    if (isCustomized && columnState && isTable) {
        mergedColumnDefs = defaultColDefs.map((colDef) => {
            const state = columnState?.find((col) => col.colId === colDef.colId);
            if (!state) return colDef;

            return {
                ...colDef,
                hide: state.hide ?? colDef.hide,
                sort: state.sort ?? colDef.sort,
                pinned: state.pinned ?? colDef.pinned,
                width: state.width ?? colDef.width,
            };
        });
    }

    return { data: mergedColumnDefs };
};

const comparator = (field1: { label: string }, field2: { label: string }) => isStringEqual(field1.label, field2.label);
const formFieldComparator = (a: Field, b: Field) => a.label.trim().toLowerCase() === b.label.trim().toLowerCase();

/**
 * Get all form fields from all forms that are not email or phone and sort them by label
 */
export const getFormUniqFields = (forms: Form[]) => {
    const EXCLUDE_HEADERS = new Set(Object.values(HeaderNames).map((header) => header.toLowerCase()));
    const labels = forms
        .flatMap((form) => form.fields)
        .filter((field) => !EXCLUDE_HEADERS.has(field.label.toLowerCase().trim() as HeaderNames));

    return uniqWith(labels, formFieldComparator).sort((a, b) =>
        a.label.toLowerCase().trim().localeCompare(b.label.toLowerCase().trim()),
    );
};
interface PickUpAddOnsUniqField {
    label: string;
    colId: string;
}
export const getPickUpAddOnsUniqFields = (experience: Experience): PickUpAddOnsUniqField[] => {
    const pickupCatalogItems = experience.catalog.items
        .filter((catalogItem) => catalogItem.unitType === UnitType.PICKUP)
        .map((catalogItem) => ({ label: catalogItem.name, colId: snakeCase(catalogItem.name) }));

    return uniqWith(pickupCatalogItems, comparator).sort((a, b) => a.label.localeCompare(b.label));
};

export const getPickUpAddOnsUniqFieldsFromMultiple = (experiences: Experience[]): PickUpAddOnsUniqField[] => {
    const allPickupItems = experiences.flatMap((experience) =>
        experience.catalog.items
            .filter((catalogItem) => catalogItem.unitType === UnitType.PICKUP)
            .map((catalogItem) => ({
                label: catalogItem.name,
                colId: snakeCase(catalogItem.name),
            })),
    );

    return uniqWith(allPickupItems, comparator).sort((a, b) => a.label.localeCompare(b.label));
};

export const columnDefs: ColDef[] = [
    {
        headerName: HeaderNames.ACTION,
        colId: RosterColumnId.ACTION,
        minWidth: 50,
        maxWidth: 80,
        sortable: false,
        lockPosition: true,
        pinned: "left",
        menuTabs: [],
        cellClass: "text-center",
        cellRenderer: ActionCellRenderer,
    },
    {
        headerName: HeaderNames.TIME,
        colId: RosterColumnId.TIME,
        minWidth: 80,
        maxWidth: 120,
        sort: "asc",
        pinned: "left",
        comparator: timeCellComparator,
        sortable: true,
        valueGetter: (params) => {
            if (Array.isArray(params.data.time) && uniq(params.data.time).length > 1) {
                return null;
            }
            return isNumber(params.data.time) ? params.data.time : uniq(params.data.time)[0];
        },
        valueFormatter: (params) => {
            if (Array.isArray(params.data.time) && uniq(params.data.time).length > 1) {
                return "Multiple Times";
            }
            if (isNumber(params.value)) {
                return formatTime(params.value, DateFormat.TIME);
            }
            return "";
        },
    },
    {
        headerName: HeaderNames.PRODUCT,
        colId: RosterColumnId.PRODUCT,
        minWidth: 150,
        sortable: true,
        pinned: "left",
        sort: "asc",
        valueGetter: (params) => {
            if (params.data.product === undefined) {
                return null;
            }
            if (Array.isArray(params.data.product) && params.data.product.length > 0) {
                return uniq(params.data.product).length;
            }
            return params.data.product;
        },
        valueFormatter: (params) => {
            if (params.data.product === undefined) {
                return "";
            }
            if (Array.isArray(params.data.product) && params.data.product.length > 0) {
                return `${uniq(params.data.product).length} products`;
            }
            return params.data.product;
        },
    },
    {
        headerName: HeaderNames.GUEST,
        colId: RosterColumnId.GUEST,
        minWidth: 200,
        sort: "asc",
        sortable: true,
        pinned: "left",
        cellRendererParams: {
            deferRender: true,
        },
        valueGetter: guestCellValueGetter,
        cellRenderer: GuestCellRenderer,
        comparator: guestCellComparator,
        filterValueGetter: (params) => (params.data && params.node ? guestCellValueGetter(params).name : ""),
    },
    {
        headerName: HeaderNames.DEMOGRAPHICS,
        colId: RosterColumnId.DEMOGRAPHICS,
        minWidth: 130,
        cellRendererParams: {
            deferRender: true,
        },
        valueGetter: demographicCellValueGetter,
        cellRenderer: DemographicCellRenderer,
    },
    {
        headerName: HeaderNames.EQUIPMENT,
        colId: RosterColumnId.EQUIPMENT,
        minWidth: 130,
        hide: true,
        cellRendererParams: {
            deferRender: true,
        },
        valueGetter: equipmentCellValueGetter,
        cellRenderer: EquipmentCellRenderer,
    },
    {
        headerName: HeaderNames.BALANCE_DUE,
        colId: RosterColumnId.BALANCE_DUE,
        minWidth: 130,
        valueGetter: amountDueCellValueGetter,
        cellRenderer: AmountDueCellRenderer,
    },
    {
        headerName: HeaderNames.GROSS_PURCHASE_AMOUNT,
        colId: RosterColumnId.GROSS_PURCHASE_AMOUNT,
        minWidth: 180,
        hide: true,
        valueGetter: grossPurchaseAmountCellValueGetter,
        valueFormatter: GrossPurchaseAmountCellFormatter,
    },
    {
        headerName: HeaderNames.GROSS_RESERVATION_AMOUNT,
        colId: RosterColumnId.GROSS_RESERVATION_AMOUNT,
        minWidth: 190,
        hide: true,
        valueGetter: grossReservationAmountCellValueGetter,
        valueFormatter: GrossReservationAmountCellFormatter,
    },
    {
        headerName: HeaderNames.NOTES,
        colId: RosterColumnId.NOTES,
        minWidth: 180,
        cellRendererParams: {
            deferRender: true,
        },
        valueGetter: notesCellValueGetter,
        cellRenderer: NotesCellRenderer,
    },
    {
        headerName: HeaderNames.CHECK_IN,
        colId: RosterColumnId.CHECK_IN,
        minWidth: 120,
        valueGetter: checkInStatusValueGetter,
        cellRenderer: CheckInStatusCellRenderer,
        cellRendererParams: {
            deferRender: true,
        },
    },
    {
        headerName: HeaderNames.WAIVERS,
        colId: RosterColumnId.WAIVERS,
        minWidth: 130,
        cellRendererParams: {
            deferRender: true,
        },
        valueGetter: waiverCellValueGetter,
        cellRenderer: WaiversCellRenderer,
    },
    {
        headerName: HeaderNames.SOURCE,
        colId: RosterColumnId.SOURCE,
        minWidth: 165,
        valueGetter: sourceCellValueGetter,
        cellRenderer: SourceCellRenderer,
    },
    {
        headerName: HeaderNames.RESERVATION_CODE,
        colId: RosterColumnId.RESERVATION_CODE,
        hide: true,
        minWidth: 200,
        valueGetter: reservationCellValueGetter,
        valueFormatter: ReservationCellFormatter,
        tooltipValueGetter: getTooltipText("Click to copy"),
        cellClass: getCellClass,
    },
    {
        headerName: HeaderNames.PURCHASE_DATE,
        colId: RosterColumnId.PURCHASE_DATE,
        hide: true,
        minWidth: 200,
        valueGetter: purchaseDateCellValueGetter,
        valueFormatter: PurchaseDateCellFormatter,
        comparator: dateComparator,
    },

    {
        headerName: HeaderNames.VOUCHERS,
        colId: RosterColumnId.VOUCHERS,
        minWidth: 150,
        valueGetter: voucherCellValueGetter,
        valueFormatter: VoucherCellFormatter,
        tooltipValueGetter: getTooltipText("Click to copy"),
        cellClass: getCellClass,
    },
    {
        headerName: HeaderNames.PHONE,
        colId: RosterColumnId.PHONE,
        minWidth: 150,
        valueGetter: phoneCellValueGetter,
        valueFormatter: PhoneCellFormatter,
        tooltipValueGetter: getTooltipText("Click to copy"),
        cellClass: getCellClass,
    },
    {
        headerName: HeaderNames.EMAIL,
        colId: RosterColumnId.EMAIL,
        minWidth: 200,
        valueGetter: emailCellValueGetter,
        tooltipValueGetter: getTooltipText("Click to copy"),
        cellClass: (value) => "no-translate " + (value ? "ui-copy-text" : ""),
    },
    {
        headerName: HeaderNames.ADD_ONS,
        colId: RosterColumnId.ADDONS,
        minWidth: 150,
        valueGetter: addOnsCellValueGetter,
        cellRenderer: AddOnsCellRenderer,
        cellRendererParams: {
            deferRender: true,
        },
    },
    {
        headerName: HeaderNames.TAGS,
        colId: RosterColumnId.TAGS,
        minWidth: 200,
        valueGetter: tagsCellValueGetter,
        valueFormatter: TagsCellFormatter,
        tooltipValueGetter: getTooltipText(),
    },
];

export const defaultColDef: ColDef = {
    sortable: true,
    resizable: true,
    menuTabs: ["generalMenuTab"],
    comparator: (valueA, valueB, nodeA, nodeB) => {
        // Prevent sorting for the summary row
        if (nodeA.data?.path?.[0] === "summary" || nodeB.data?.path?.[0] === "summary") {
            return 0;
        }

        // Prevent sorting for rows of different hierarchy levels
        if (nodeA.level !== nodeB.level) {
            return 0;
        }

        const isStrings = typeof valueA === "string" && typeof valueB === "string";

        // Sort organizer rows by their name
        if (isStrings && nodeA.level === 1 && nodeB.level === 1) {
            return valueA.localeCompare(valueB);
        }

        // Sort guest rows under the same organizer
        if (isStrings && nodeA.level === 2 && nodeB.level === 2 && nodeA.parent === nodeB.parent) {
            return valueA.localeCompare(valueB);
        }

        if (isStrings) {
            if (valueA === valueB) return 0;
            return valueA.toUpperCase() > valueB?.toUpperCase() ? 1 : -1;
        }

        if (typeof valueA === "number" && typeof valueB === "number") {
            return valueA - valueB;
        }

        return 0;
    },
};

export const autoGroupColumnDef: ColDef = {
    headerName: "",
    maxWidth: 50,
    showRowGroup: false,
    lockPosition: true,
    pinned: "left",
    menuTabs: [],
    // @ts-expect-error ts arguing on it, but it exists and working
    headerComponentSelector: (params: any) => {
        if (params.context?.allAttendeesUnique) {
            return { component: CollapseHeaderComponent };
        }

        return null;
    },
    cellRendererParams: {
        suppressCount: true,
        innerRenderer: (params: any) => {
            const isSubSummaryRow = Boolean(params.data?.rosterSection);

            if (params.node.key === "summary" || params.data.isOrganizer || isSubSummaryRow) {
                const rowNodeIndex = params.node.rowIndex;
                const isAttendeeUnique = params.data.isAttendeeUnique;

                if (
                    !isAttendeeUnique &&
                    !isSubSummaryRow &&
                    !isEquipmentItem(params.data.item) &&
                    params.node.key !== "summary"
                ) {
                    return null;
                }

                const onClickExpandIcon = () => {
                    params.api.refreshCells(params);
                    const row = params.api.getDisplayedRowAtIndex(rowNodeIndex ?? 0);

                    const redrawRowsParams: RedrawRowsParams | null = row ? { rowNodes: [row] } : null;
                    if (redrawRowsParams) {
                        params.api.redrawRows(redrawRowsParams);
                    }
                };

                return (
                    <ChevronRightIcon
                        className={clsx(
                            "h-[16px] w-[12px] cursor-pointer stroke-[1.5px]",
                            params.node.expanded && "rotate-90",
                        )}
                        onClick={onClickExpandIcon}
                    />
                );
            }

            return null;
        },
    },
};

export const getColumnIndexById = (columnDefs: ColDef[], id: RosterColumnId) => {
    return columnDefs.findIndex((colDef) => colDef.colId === id);
};

export interface DefaultColumnDefsOrderInterface {
    colId: RosterColumnId;
    sortable?: boolean;
    lockPosition?: boolean;
    pinned?: string | null | undefined;
    suppressMenu?: boolean;
    hide: boolean;
    minWidth?: number;
    sort?: "asc" | "desc" | null;
}

export const defaultColumnDefsOrder: DefaultColumnDefsOrderInterface[] = [
    {
        colId: RosterColumnId.ACTION,
        sortable: false,
        lockPosition: true,
        pinned: "left",
        suppressMenu: true,
        hide: false,
    },
    {
        colId: RosterColumnId.TIME,
        sort: "asc",
        sortable: true,
        lockPosition: true,
        pinned: "left",
        suppressMenu: true,
        hide: false,
    },
    {
        colId: RosterColumnId.PRODUCT,
        sort: "asc",
        sortable: true,
        lockPosition: true,
        pinned: "left",
        suppressMenu: true,
        minWidth: 150,
        hide: false,
    },
    {
        colId: RosterColumnId.GUEST,
        sort: "asc",
        sortable: true,
        lockPosition: true,
        pinned: "left",
        minWidth: 200,
        suppressMenu: true,
        hide: false,
    },
    {
        colId: RosterColumnId.DEMOGRAPHICS,
        hide: false,
        pinned: null,
        minWidth: 130,
        sort: null,
    },
    {
        colId: RosterColumnId.BALANCE_DUE,
        hide: false,
        pinned: null,
        sort: null,
    },
    {
        colId: RosterColumnId.GROSS_PURCHASE_AMOUNT,
        hide: true,
        pinned: null,
        sort: null,
    },
    {
        colId: RosterColumnId.GROSS_RESERVATION_AMOUNT,
        hide: true,
        pinned: null,
        sort: null,
    },
    {
        colId: RosterColumnId.NOTES,
        hide: false,
        pinned: null,
        sort: null,
    },
    {
        colId: RosterColumnId.CHECK_IN,
        hide: false,
        pinned: null,
        sort: null,
    },
    {
        colId: RosterColumnId.WAIVERS,
        hide: false,
        pinned: null,
        sort: null,
    },
    {
        colId: RosterColumnId.SOURCE,
        hide: false,
        pinned: null,
        sort: null,
    },
    {
        colId: RosterColumnId.RESERVATION_CODE,
        hide: true,
        pinned: null,
        sort: null,
    },
    {
        colId: RosterColumnId.PURCHASE_DATE,
        hide: true,
        pinned: null,
        sort: null,
    },
    {
        colId: RosterColumnId.VOUCHERS,
        hide: false,
        pinned: null,
        sort: null,
    },
    {
        colId: RosterColumnId.PHONE,
        hide: false,
        pinned: null,
        sort: null,
    },
    {
        colId: RosterColumnId.EMAIL,
        hide: false,
        pinned: null,
        sort: null,
    },
    {
        colId: RosterColumnId.ADDONS,
        hide: false,
        pinned: null,
        sort: null,
    },
    {
        colId: RosterColumnId.TAGS,
        hide: false,
        pinned: null,
        sort: null,
    },
];
